import { useCallback, useEffect, useRef } from "react";

/**
 * useRunGuard hook manages the lifecycle of asynchronous operations.
 * It tracks whether the component is mounted and prevents state updates from stale runs.
 * It also manages AbortControllers to cancel in-progress HTTP requests.
 */
export function useRunGuard() {
  const mountedRef = useRef(true);
  const activeRunIdRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      activeRunIdRef.current += 1;
      abortControllerRef.current?.abort();
    };
  }, []);

  const startRun = useCallback(() => {
    abortControllerRef.current?.abort();
    
    abortControllerRef.current = new AbortController();
    activeRunIdRef.current += 1;
    return {
      runId: activeRunIdRef.current,
      abortController: abortControllerRef.current
    };
  }, []);

  const isActiveRun = useCallback((runId: number) => {
    return mountedRef.current && activeRunIdRef.current === runId;
  }, []);

  const cancelCurrentRun = useCallback(() => {
    abortControllerRef.current?.abort();
    activeRunIdRef.current += 1;
  }, []);

  const resetRunState = useCallback(() => {
    activeRunIdRef.current += 1;
    abortControllerRef.current = null;
  }, []);

  return {
    startRun,
    isActiveRun,
    cancelCurrentRun,
    resetRunState
  };
}
